import { useEffect, useId, useState, type FormEvent, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  CONTACT_FIELDS,
  contactPatch,
  whatsappError,
  type ContactDetails,
  type ContactPatch,
} from '@/lib/contactDetails';

/**
 * Edit an outreacher's contact details. Used by the outreacher on My details
 * and by admins on the outreacher's profile; `onSave` gets only what changed.
 */
export function ContactDetailsForm({
  value,
  onSave,
  pending,
}: {
  value: ContactDetails;
  onSave: (patch: ContactPatch) => Promise<unknown>;
  pending: boolean;
}): JSX.Element {
  const id = useId();
  const [draft, setDraft] = useState(value);
  // Follow the saved value when it changes underneath (after a save, or when
  // an admin switches who they are viewing as).
  useEffect(() => setDraft(value), [value]);

  const patch = contactPatch(value, draft);
  const dirty = Object.keys(patch).length > 0;
  const numberError = whatsappError(draft.contactWhatsapp);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!dirty || numberError || pending) return;
    void onSave(patch);
  }

  return (
    <form onSubmit={submit} className="panel space-y-4 rounded-xl p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {CONTACT_FIELDS.map((f) => {
          const fieldId = `${id}-${f.key}`;
          const error = f.key === 'contactWhatsapp' ? numberError : null;
          const describedBy = error ? `${fieldId}-error` : f.hint ? `${fieldId}-hint` : undefined;
          return (
            <div key={f.key} className={cn('space-y-1.5', f.multiline && 'sm:col-span-2')}>
              <Label htmlFor={fieldId}>{f.label}</Label>
              {f.multiline ? (
                <textarea
                  id={fieldId}
                  rows={3}
                  maxLength={1000}
                  value={draft[f.key]}
                  placeholder={f.placeholder}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  className={cn(
                    'flex w-full rounded-lg border border-input bg-[var(--input-bg)] px-3 py-2 text-sm shadow-control',
                    'placeholder:text-muted-foreground/70 pointer-coarse:text-base',
                    'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25',
                  )}
                />
              ) : (
                <Input
                  id={fieldId}
                  type={f.key === 'contactWhatsapp' ? 'tel' : 'text'}
                  autoComplete={f.key === 'contactWhatsapp' ? 'tel' : 'off'}
                  maxLength={f.key === 'contactWhatsapp' ? 40 : 120}
                  value={draft[f.key]}
                  placeholder={f.placeholder}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={describedBy}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
              )}
              {error ? (
                <p id={`${fieldId}-error`} role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              ) : (
                f.hint && (
                  <p id={`${fieldId}-hint`} className="text-xs text-muted-foreground">
                    {f.hint}
                  </p>
                )
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!dirty || !!numberError || pending}>
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save
        </Button>
        {dirty && !pending && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(value)}>
            Discard changes
          </Button>
        )}
      </div>
    </form>
  );
}
